import { useState, useEffect } from 'react'
import Modal from './Modal'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { apiService } from '@/service/api'
import { toast } from 'sonner'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import { trackEvent } from '@/utils/analytics'
import { Check } from 'lucide-react'
import clsx from 'clsx'
import type { Agent } from '@/components/AgentSelector'
import { Checkbox } from '@/components/ui/checkbox'
import { IntuiSwitch } from '@/components/intueri/IntuiSwitch'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'

interface CallConfigModalProps {
  isOpen: boolean
  onClose: () => void
}

const CallConfigModal = ({ isOpen, onClose }: CallConfigModalProps) => {
  const { t } = useTranslation()
  const [meetingUrl, setMeetingUrl] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [translatorMode, setTranslatorMode] = useState(false)
  // "Use Entire Data Catalog" for this call — the voice counterpart of the chat footer
  // toggle. Off = the bot answers from the board attached to the meeting (today's behaviour).
  // Fixed for the call's lifetime: it is persisted on the meeting token at start.
  const [entireDataCatalog, setEntireDataCatalog] = useState(false)
  const { enabled: jnTranslationEnabled } = useApiFeatureFlag('jn_translation_enabled')
  const { context_id, agents, fetchAgents, boardList, createBoard, generateDefaultBoardName, setContextId, selectedAgentForCall } = useAppStore()

  // Fetch agents when modal opens
  useEffect(() => {
    if (isOpen && fetchAgents) {
      fetchAgents()
    }
  }, [isOpen, fetchAgents])

  // Initialize selectedAgent from store when modal opens
  useEffect(() => {
    if (isOpen && selectedAgentForCall) {
      setSelectedAgent(selectedAgentForCall)
    }
  }, [isOpen, selectedAgentForCall])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedAgent(null)
      setMeetingUrl('')
      setTranslatorMode(false)
      setEntireDataCatalog(false)
    }
  }, [isOpen])

  // Filter only agents available for meeting
  const availableAgents = (agents || []).filter(
    (agent) => agent.availableForMeeting
  )

  const handleSubmit = async () => {
    if (!selectedAgent) {
      toast.error(t('chat:call_config.errors.no_agent_selected'))
      return
    }

    if (!meetingUrl.trim()) {
      toast.error(t('chat:call_config.errors.no_meeting_link'))
      return
    }

    setIsLoading(true)
    try {
      // Get or create a board if context_id is not set. FS-553: when the user
      // has no board we create their first one on demand (a normal, named
      // board — no more auto-created Master Board).
      let workspaceId = context_id
      if (!workspaceId) {
        if (boardList.length > 0) {
          const sortedBoards = [...boardList].sort((a, b) => {
            const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
            const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
            return timeB - timeA
          })
          workspaceId = sortedBoards[0].id
          setContextId(workspaceId)
        } else {
          const newBoard = await createBoard(generateDefaultBoardName())
          if (newBoard) {
            workspaceId = newBoard.id
            setContextId(workspaceId)
          } else {
            toast.error(t('chat:interface.errors.select_workspace'))
            setIsLoading(false)
            return
          }
        }
      }

      const webapp_url = `${window.location.origin}/meeting`
      const agent_id = selectedAgent.id
      await apiService.startMeeting(
        meetingUrl,
        webapp_url,
        agent_id,
        workspaceId,
        undefined,
        translatorMode,
        entireDataCatalog
      )
      const url = meetingUrl.toLowerCase()
      const platform = url.includes('meet.google.com') ? 'google_meet'
        : url.includes('teams.microsoft.com') || url.includes('teams.live.com') ? 'ms_teams'
        : url.includes('zoom.us') ? 'zoom'
        : 'unknown'
      trackEvent('ai_call-started', {
        agent_id: selectedAgent.id,
        agent_name: selectedAgent.name,
        meeting_platform: platform,
        translator_mode_enabled: translatorMode,
        entire_data_catalog_enabled: entireDataCatalog,
      })
      toast.success(
        t('chat:call_config.toasts.adding_agent', {
          agentName: selectedAgent.name,
        })
      )
      setMeetingUrl('')
      setSelectedAgent(null)
      onClose()
    } catch (error) {
      console.error('Error starting call:', error)
      const errorMessage = error instanceof Error ? error.message : ''
      trackEvent('ai_call-failed', {
        agent_id: selectedAgent.id,
        error_type: errorMessage || 'unknown',
      })
      if (errorMessage === 'BOT_ALREADY_EXISTS') {
        toast.error(t('chat:call_config.errors.bot_already_exists'))
      } else {
        toast.error(t('chat:call_config.errors.failed_to_start'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('chat:call_config.title')}
      description={t('chat:call_config.description')}
      className="max-w-[680px]"
      footer={
        <>
          <Button variant="outline" onClick={onClose} className="px-6">
            {t('chat:call_config.button_cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !selectedAgent || !meetingUrl.trim()}
            className="px-6"
          >
            {isLoading ? t('chat:call_config.button_sending') : t('chat:call_config.button_send_invite')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5 py-2">
        {/* Agent Selection */}
        <div>
          <label className="text-sm font-semibold text-gray-900 dark:text-dark-text mb-3 block">
            {t('chat:call_config.select_agent_label')}
          </label>
          <div className="grid grid-cols-4 gap-3">
            {availableAgents.map((agent) => {
              const isSelected = selectedAgent?.id === agent.id
              
              // Agent-specific background colors (matching intelligent-board)
              const agentColors: Record<string, string> = {
                'plant-support': '#FEF08A',
                'chemical-agent': '#BFDBFE',
                'mechanical-agent': '#BBF7D0',
                'electrical-agent': '#E9D5FF',
              }
              const bgColor = agentColors[agent.id] || '#F3F4F6'
              
              return (
                <button
                  key={agent.id}
                  onClick={() => {
                    setSelectedAgent(agent)
                    trackEvent('ai_call-agent_selected', { agent_id: agent.id, agent_name: agent.name })
                  }}
                  className={clsx(
                    'relative flex flex-col items-center py-3 px-3 rounded-xl border-2 transition-all',
                    isSelected
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-dark-card'
                  )}
                >
                  {/* Selection checkmark */}
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                  {/* Agent avatar with colored background */}
                  <div 
                    className="w-16 h-16 rounded-full overflow-hidden mb-2 flex items-center justify-center shadow-md"
                    style={{ backgroundColor: bgColor }}
                  >
                    <img
                      src={agent.image}
                      alt={agent.name}
                      className="w-16 h-16 object-cover rounded-full"
                    />
                  </div>
                  {/* Agent title */}
                  <span className="font-bold text-gray-900 dark:text-dark-text text-sm text-center">
                    {agent.title}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Meeting Link Input */}
        <div>
          <label className="text-sm font-semibold text-gray-900 dark:text-dark-text mb-2 block">
            {t('chat:call_config.meeting_link_label')}
          </label>
          <Input
            className="w-full bg-gray-100 dark:bg-dark-accent border-0 h-10"
            placeholder={t('chat:call_config.placeholder_meeting_url')}
            type="text"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
          />
          <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1.5">
            {t('chat:call_config.meeting_platforms')}
          </p>
        </div>

        {/* Retrieval scope for the whole call. Same label as the chat footer toggle, and the
            same default (off = the meeting's board is the context).

            Hidden in a Fabric embed, for the reason embedFeatures.ts gives for keeping
            `catalogScope` off the allowlist: the host decides which documents an embedded
            board may see, and ticking this resolves the roster from
            `resolveCatalogRoster(tenantId)` — the whole tenant, with no host-granted
            intersection. The value is persisted on the meeting token, so it is fixed for the
            call's lifetime and cannot be narrowed later. Hiding it here covers BOTH ways into
            this modal in an embed: the video dropdown, and the board top bar's ungated
            "Call AI Engineer" button. `entireDataCatalog` stays false, so the call still
            starts, board-scoped. */}
        {isFeatureAllowedInEmbed('catalogScope') && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="entire-data-catalog"
              data-testid="entire-data-catalog-checkbox"
              checked={entireDataCatalog}
              onCheckedChange={(checked) => setEntireDataCatalog(checked === true)}
            />
            <label
              htmlFor="entire-data-catalog"
              className="text-sm font-medium text-gray-900 dark:text-dark-text cursor-pointer"
            >
              {t('chat:call_config.entire_data_catalog')}
            </label>
          </div>
        )}

        {jnTranslationEnabled && (
          <div className="border-t border-gray-200 dark:border-dark-border pt-4">
            <IntuiSwitch
              id="translator-mode-toggle"
              checked={translatorMode}
              onCheckedChange={setTranslatorMode}
              label={t('chat:call_config.translator_mode_label')}
              description={t('chat:call_config.translator_mode_description')}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default CallConfigModal
